import { DateTimePicker, PopoverOpenButton } from "@msys/ui";
import { KeyboardArrowDown as KeyboardArrowDownIcon } from "@mui/icons-material";
import { Box, Button, Stack } from "@mui/material";
import moment from "moment";
import React from "react";
import { browserHasInputDateTime } from "../../featureDetection.js";

interface Props {
  /** Trigger-button label (e.g. "Time"). */
  label: string;
  fromLabel: string;
  toLabel: string;
  /** Inclusive lower bound, ISO datetime, or null. */
  from: string | null;
  /** Inclusive upper bound, ISO datetime, or null. */
  to: string | null;
  onChange: (next: { from: string | null; to: string | null }) => void;
  width?: number;
}

/**
 * Occurred-time range as a dropdown — a labelled "Time" button (mirroring the
 * `ChangeLogFilterDropdown` look) opening a popover with two compact
 * `DateTimePicker`s (From / To, the `datetime-local` control the planning
 * "Schedule session" row uses). Each picked moment is the exact inclusive bound,
 * serialised verbatim with `toISOString()` (no day rounding); clearing a picker
 * sends `null`. The button shows the count of set bounds, like the other filters.
 *
 * `fromLabel` / `toLabel` are passed as each picker's own `label`, not rendered as a
 * separate caption above it: a detached `Typography` is not associated with the
 * input, so with `label=""` both controls would have NO accessible name and a screen
 * reader could not tell From from To. This is how `ResourceFilterModal` labels the
 * same From/To pair.
 */
export function ChangeLogDateRangeDropdown({
  label,
  fromLabel,
  toLabel,
  from,
  to,
  onChange,
  width = 280,
}: Props) {
  const [open, setOpen] = React.useState(false);
  const count = (from ? 1 : 0) + (to ? 1 : 0);

  return (
    <PopoverOpenButton
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      transformOrigin={{ vertical: "top", horizontal: "left" }}
      content={
        <Box sx={{ p: 1.5, width }}>
          <Stack direction="column" spacing={1.5}>
            <DateTimePicker
              browserHasInputDateTime={browserHasInputDateTime}
              isClearable
              label={fromLabel}
              value={from ? moment(from) : null}
              onChange={(_e, m) =>
                onChange({ from: m ? m.toISOString() : null, to })
              }
              textFieldProps={{
                name: "from",
                fullWidth: true,
                size: "extra-small",
              }}
            />
            <DateTimePicker
              browserHasInputDateTime={browserHasInputDateTime}
              isClearable
              label={toLabel}
              value={to ? moment(to) : null}
              onChange={(_e, m) =>
                onChange({ from, to: m ? m.toISOString() : null })
              }
              textFieldProps={{
                name: "to",
                fullWidth: true,
                size: "extra-small",
              }}
            />
          </Stack>
        </Box>
      }
    >
      <Button
        size="extra-small"
        variant="text"
        color="primary"
        sx={{ textTransform: "none" }}
        endIcon={
          <KeyboardArrowDownIcon
            sx={{
              transition: "transform 0.2s ease-out",
              ...(open ? { transform: "rotate(-180deg)" } : undefined),
            }}
          />
        }
      >
        {label}
        {count > 0 ? ` (${count})` : ""}
      </Button>
    </PopoverOpenButton>
  );
}
